import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useIsVendor } from "~/shared/hooks/use-is-vendor.ts";
import type { Feedback } from "~/shared/schemas/feedback-schema";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { postAndValidate } from "~/shared/services/api/fetch-client.ts";
import { pickEndpoint } from "~/shared/services/api/pick-endpoint.ts";

export const postSubmitFeedback = async (
	payload: Feedback,
	isInternal: boolean,
	isVendor: boolean,
	signal?: AbortSignal,
): Promise<unknown> => {
	const url = pickEndpoint(
		{
			internal: apiRoutes.SUBMITINTERNALFEEDBACK as string,
			external: apiRoutes.SUBMITFEEDBACK as string,
			vendor: apiRoutes.SUBMITVENDORFEEDBACK as string,
		},
		{ isInternal, isVendor },
	);
	const resp = await postAndValidate(url, payload, z.unknown(), {
		signal,
	});

	if (resp.success) {
		return resp.data;
	}

	throw new Error(`Failed to save feedback: ${JSON.stringify(resp.error)}`);
};

export const useSubmitFeedback = () => {
	const { isInternal } = useDeploymentType();
	const { isVendor } = useIsVendor();

	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation<
		unknown,
		Error,
		Feedback
	>({
		mutationFn: (payload: Feedback) =>
			postSubmitFeedback(payload, isInternal, isVendor),
	});

	return {
		submitFeedback: mutate,
		submitFeedbackAsync: mutateAsync,
		isSubmitting: isPending,
		reset,
		error,
		data,
	};
};
